/**
 * Person setup (board onboarding-r2): the steps on a rail, the step's own panel, and the computer beside it. Every
 * founder and teammate runs it; a founder who starts their own computer sees "Start your computer" first. The server
 * keeps the progress, so a rerun from the dashboard walks it again as a tutorial: done steps say when, and nothing
 * real is undone. Each step URL is /setup/<key>.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'
import { addressOf, FIRST_ASK, LATER_ASKS, PASEO_WEB, type Ask } from '../../domain/copy'
import { CHECK_LABELS, lineOk } from '../../domain/machine'
import type { GeoKey } from '../../domain/plans'
import type { AgentName, CheckKey, CodexLogin, Connection, ShopifyMode } from '../../domain/schemas'
import {
  canView,
  doneOn,
  firstOpenStep,
  isStepKey,
  markable,
  needsProvision,
  nextStep,
  prevStep,
  SETUP_STEPS,
  STEP_NAME,
  stepDone,
  stepGate,
  stepState,
  tutorialMode,
  visibleSteps,
  type SetupStepKey,
} from '../../domain/setup'
import { ApiError } from '../../infra/api'
import { visibleInterval } from '../../infra/visibleInterval'
import { Check, Out } from '../components/icons'
import { PlanPicker, usePlanRegion } from '../components/PlanPicker'
import { ChoiceCard, Gate, LockNote, Meter, Mono, Notice, Verify } from '../components/ui'
import { useLoad } from '../useLoad'
import { useFounder } from './context'
import { AddressCard, PreviewNote, SetupStrip } from './Machine'
import { ClaudeSignIn, CopyButton, GetPaseo, PairPanel, ShopifyConnect, ShopifyConnected, ShopifyGuide } from './panels'

const KIND: Record<SetupStepKey, string> = Object.fromEntries(SETUP_STEPS.map((s) => [s.key, s.kind])) as Record<SetupStepKey, string>

export function SetupPage() {
  const f = useFounder()
  const { step } = useParams()
  if (!isStepKey(step) || !visibleSteps(f.setup).includes(step)) return <Navigate to={`/setup/${firstOpenStep(f.setup)}`} replace />
  if (!canView(step, f.setup)) return <Navigate to="/setup/region" replace />

  const keys = visibleSteps(f.setup)
  const gate = stepGate(step, f.lines)
  const waitsFor = gate.locked && gate.waitingFor ? { key: gate.waitingFor, step: `Step ${keys.indexOf(step) + 1}` } : null

  return (
    <div className="setup">
      <Rail current={step} />
      <PhoneSteps current={step} />
      <aside className="setup-aside" aria-label="Your computer">
        <SetupStrip waitsFor={waitsFor} />
        <AddressCard />
      </aside>
      <main className="setup-panel">
        <Lead />
        <StepPanel step={step} />
      </main>
    </div>
  )
}

/**
 * Above the step: on a rerun, what tutorial mode means; for a founder whose computer we set up, their live site and
 * what comes next.
 */
function Lead() {
  const f = useFounder()
  if (tutorialMode(f.setup)) return <Notice>You're running setup again. Steps you've done say when, and nothing you've set up is undone.</Notice>
  if (f.personSetup.path !== 'whiteglove') return null
  const host = addressOf(f.state.world)
  const live = host && lineOk(f.lines, 'address')
  return (
    <Verify
      title={live ? `Your site is live at ${host}.` : host ? `Your site goes live at ${host} in a few minutes.` : 'We set up your computer and your app for you.'}
      sub="Now let's set you up to change it: pair a device, sign in your agent, change something, and ship it."
    />
  )
}

function Rail({ current }: { current: SetupStepKey }) {
  const f = useFounder()
  const nav = useNavigate()
  const keys = visibleSteps(f.setup)
  const open = firstOpenStep(f.setup)
  const done = keys.filter((k) => stepDone(k, f.setup)).length
  return (
    <nav className="rail" aria-label="Setup steps">
      <div className="rail-box">
        <div className="rail-head">
          <strong>{tutorialMode(f.setup) ? 'Setup again' : 'Setup'}</strong>
          <span className="label">{keys.length} steps</span>
        </div>
        <ol>
          {keys.map((k, i) => {
            const isDone = stepDone(k, f.setup)
            const cls = ['ri', isDone ? 'done' : '', k === open && !isDone ? 'now' : '', k === current ? 'viewing' : ''].join(' ')
            return (
              <li key={k}>
                <button className={cls} type="button" disabled={!canView(k, f.setup)} aria-current={k === current ? 'step' : undefined} onClick={() => nav(`/setup/${k}`)}>
                  <span className="bullet">{isDone ? <Check size={14} /> : <span>{i + 1}</span>}</span>
                  <span>
                    <span className="ri-name">{STEP_NAME[k]}</span>
                    <span className="kind">{stepState(k, f.setup).status === 'skipped' ? 'Later' : KIND[k]}</span>
                  </span>
                </button>
              </li>
            )
          })}
        </ol>
        <div className="rail-auto">
          <span className={`s-dot ${f.progress.ready ? 'ok' : f.progress.started ? 'run' : 'idle'}`}>{f.progress.ready && <Check size={11} />}</span>
          <span>
            We set up your computer
            <span className="kind">
              Automatic · {f.progress.done} of {f.progress.total}
            </span>
          </span>
        </div>
        <div className="rail-foot">
          <Meter value={Math.round((done / keys.length) * 100)} />
          <span className="label">{done === keys.length ? 'Set up' : `${done} of ${keys.length} done`}</span>
        </div>
      </div>
    </nav>
  )
}

/** On a phone the rail becomes segments under one line. */
function PhoneSteps({ current }: { current: SetupStepKey }) {
  const f = useFounder()
  const nav = useNavigate()
  const keys = visibleSteps(f.setup)
  return (
    <nav className="phone-steps" aria-label="Setup steps">
      <div className="ps-label">
        <span className="label">
          Step {keys.indexOf(current) + 1} of {keys.length}
        </span>
        <b>{STEP_NAME[current]}</b>
      </div>
      <div className="ps-segs">
        {keys.map((k, n) => (
          <button
            key={k}
            type="button"
            className={[stepDone(k, f.setup) ? 'done' : '', k === current ? 'viewing' : ''].join(' ')}
            disabled={!canView(k, f.setup)}
            aria-label={`Step ${n + 1}, ${STEP_NAME[k]}${stepDone(k, f.setup) ? ', done' : ''}`}
            aria-current={k === current ? 'step' : undefined}
            onClick={() => nav(`/setup/${k}`)}
          />
        ))}
      </div>
    </nav>
  )
}

/** The step's sheet. Its tag says the kind, or when it was done ("Done 13 Oct"). */
function Sheet({ step, children, nav }: { step: SetupStepKey; children: ReactNode; nav?: ReactNode }) {
  const f = useFounder()
  const keys = visibleSteps(f.setup)
  const st = stepState(step, f.setup)
  const when = doneOn(st.at)
  const tag = st.status === 'done' ? (when ? `Done ${when}` : 'Done') : st.status === 'skipped' ? 'Left for later' : KIND[step]
  return (
    <section className="sheet step-sheet" aria-labelledby={`h-${step}`}>
      <div className="step-head">
        <span className="label">
          Step {keys.indexOf(step) + 1} of {keys.length}
        </span>
        <span className="kind-tag">{tag}</span>
      </div>
      {children}
      {nav && <div className="step-nav">{nav}</div>}
    </section>
  )
}

/**
 * Back, "Do this later" (the step is marked skipped) and Continue (marked done, unless a real signal already ticked
 * it). A mark that fails to save still moves on: the server asks again next time.
 */
function StepNav({ step, canContinue, later, label = 'Continue' }: { step: SetupStepKey; canContinue: boolean; later?: boolean; label?: string }) {
  const f = useFounder()
  const nav = useNavigate()
  const prev = prevStep(step, f.setup)
  const next = nextStep(step, f.setup)
  const st = stepState(step, f.setup)
  const go = (status: 'done' | 'skipped' | null) => {
    if (status && markable(step) && st.how !== 'observed' && st.status !== status) void f.mark(step, status).catch(() => {})
    if (next) nav(`/setup/${next}`)
  }
  return (
    <>
      {prev && canView(prev, f.setup) ? (
        <button className="btn btn-ghost" type="button" onClick={() => nav(`/setup/${prev}`)}>
          Back
        </button>
      ) : (
        <span />
      )}
      <span className="spacer" />
      {later && !canContinue && next && (
        <button className="btn btn-ghost" type="button" onClick={() => go('skipped')}>
          Do this later
        </button>
      )}
      {canContinue && next && (
        <button className="btn btn-ink" type="button" onClick={() => go('done')}>
          {label}
        </button>
      )}
    </>
  )
}

/** "Phone link ready is the check running now." / "… is 3 checks away." */
function togo(lines: ReturnType<typeof useFounder>['lines'], key: CheckKey): string {
  const target = lines.findIndex((l) => l.key === key)
  const current = lines.findIndex((l) => l.status !== 'ok')
  const left = target - current
  return left <= 0 ? `${CHECK_LABELS[key]} is the check running now.` : `${CHECK_LABELS[key]} is ${left === 1 ? 'one check' : `${left} checks`} away.`
}

function StepPanel({ step }: { step: SetupStepKey }) {
  switch (step) {
    case 'region':
      return <RegionStep />
    case 'signin':
      return <SigninStep />
    case 'phone':
      return <PhoneStep />
    case 'agent':
      return <AgentStep />
    case 'build':
      return <BuildStep />
    case 'data':
      return <DataStep />
    case 'ship':
      return <ShipStep />
  }
}

function RegionStep() {
  const f = useFounder()
  const nav = useNavigate()
  const needs = needsProvision(f.state.world)
  const plans = useLoad(() => (needs ? f.api.plans() : Promise.resolve(null)), [f.api, needs])
  const region = usePlanRegion(plans.data ?? null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  async function start() {
    if (!region.value) return
    setBusy(true)
    setError(undefined)
    try {
      await f.api.provision(region.value as GeoKey)
      await f.reload()
      nav('/setup/signin')
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        await f.reload()
        nav('/setup/signin')
      } else {
        setError("We couldn't start your computer just now. Try again in a moment, or flag us.")
        setBusy(false)
      }
    }
  }

  if (!needs) {
    return (
      <Sheet step="region" nav={<StepNav step="region" canContinue />}>
        <h1 id="h-region">{f.progress.ready ? 'Your computer is ready.' : 'Your computer is on its way.'}</h1>
        <p className="lede">
          {f.progress.ready
            ? 'It runs in a data centre near you, and it is yours to build on.'
            : 'It is being set up in a data centre near you. You can carry on with the next steps while it builds.'}
        </p>
      </Sheet>
    )
  }
  return (
    <Sheet step="region">
      <h1 id="h-region">Pick where your computer lives.</h1>
      <p className="lede">Your computer runs in a data centre. The closest one keeps your agent quick. It starts building the moment you confirm.</p>
      <div className="block">
        {plans.error && <Notice>We couldn't load the regions just now. Try again in a moment.</Notice>}
        {plans.data && <PlanPicker plans={plans.data} value={region.value} suggested={region.suggested} onChange={region.setValue} />}
        {!plans.data && !plans.error && <p className="aside">One moment.</p>}
      </div>
      {error && <Notice>{error}</Notice>}
      <div className="step-nav">
        <span className="spacer" />
        <button className="btn btn-ink" type="button" onClick={start} disabled={!region.value || busy}>
          {busy ? 'Starting…' : 'Start my computer'}
        </button>
      </div>
    </Sheet>
  )
}

function SigninStep() {
  const f = useFounder()
  return (
    <Sheet step="signin" nav={<StepNav step="signin" canContinue />}>
      <h1 id="h-signin">You're signed in.</h1>
      <p className="lede">This page is how you see your computer and your app, from any device. The next steps set you up to build.</p>
      <Verify title={`Signed in as ${f.me.person.email}.`} sub={f.personSetup.path === 'teammate' ? `On ${f.state.world.founder.name}'s computer, with your own sign-ins.` : undefined} />
    </Sheet>
  )
}

/**
 * Pairing. The step ticks when the computer's Paseo has seen a device (agent 0.4.0). An older agent can't count, so
 * "I'm paired" marks it; with a count, "Paired, but no tick?" stays as a quiet way past a count that hasn't caught up.
 * Once the link is up, the step asks for progress every few seconds instead of every 20, so the tick lands as soon
 * as the computer reports the device.
 */
function PhoneStep() {
  const f = useFounder()
  const gate = stepGate('phone', f.lines)
  const st = stepState('phone', f.setup)
  const devices = f.personSetup.devices ?? f.state.world.devices
  const [shown, setShown] = useState(false)
  const done = st.status === 'done'
  const counted = devices !== null
  const { reloadSetup } = f
  useEffect(() => (shown && !done ? visibleInterval(() => void reloadSetup(), 4000) : undefined), [shown, done, reloadSetup])
  return (
    <Sheet step="phone" nav={<StepNav step="phone" canContinue={done || shown} later label={done ? 'Continue' : counted ? 'Paired, but no tick? Continue' : "I'm paired"} />}>
      <h1 id="h-phone">{done && tutorialMode(f.setup) ? 'Pair another device.' : 'Pair a device.'}</h1>
      <p className="lede">Paseo is the app you build from, on your phone, iPad or laptop. It's open source and needs no account. Pair as many devices as you like.</p>
      {done && st.how === 'observed' && <Verify title={`Your computer has seen ${st.detail ?? 'your device'}.`} sub="Each device you pair shows up here within a minute." />}
      <div className="block">
        <div className="block-title">Get Paseo, then pair it with your computer</div>
        <GetPaseo />
        {gate.locked ? (
          <Gate title="Your computer is almost ready.">Pairing waits for Paseo to start on it. {togo(f.lines, 'phone')} Get the app while you wait.</Gate>
        ) : (
          <PairPanel onShown={() => setShown(true)} />
        )}
        {shown && !done && counted && <Gate title="Waiting for your device.">This ticks a few seconds after Paseo says it's paired. Your project is already there.</Gate>}
      </div>
    </Sheet>
  )
}

const AGENT_CARDS: { key: AgentName; name: string; plan: string }[] = [
  { key: 'claude', name: 'Claude', plan: 'Runs on your Claude Pro or Max plan.' },
  { key: 'codex', name: 'Codex', plan: 'Runs on your ChatGPT plan.' },
]

/** Pick a coding agent (Claude preselected) and sign it in on the computer. Switching later keeps the old sign-in. */
function AgentStep() {
  const f = useFounder()
  const gate = stepGate('agent', f.lines)
  const agent = f.personSetup.agent
  const done = stepDone('agent', f.setup)
  const [error, setError] = useState<string>()
  const pick = (a: string) => {
    setError(undefined)
    if (a !== agent) f.pickAgent(a as AgentName).catch(() => setError("We couldn't save your pick just now. Try again in a moment."))
  }
  const other = agent === 'claude' && f.claude?.loggedIn && f.claude.email && f.claude.email.toLowerCase() !== f.me.person.email.toLowerCase() ? f.claude.email : null
  return (
    <Sheet step="agent" nav={<StepNav step="agent" canContinue={done} later />}>
      <h1 id="h-agent">Pick your agent and sign in.</h1>
      <p className="lede">Your agent writes the code, on your own plan, on your own computer. You can switch later; the other sign-in stays.</p>
      <div className="block choices" role="radiogroup" aria-label="Coding agent">
        {AGENT_CARDS.map((c) => (
          <ChoiceCard key={c.key} name="agent" value={c.key} checked={agent === c.key} onPick={pick}>
            <b>{c.name}</b>
            <span>{c.plan}</span>
          </ChoiceCard>
        ))}
      </div>
      {error && <Notice>{error}</Notice>}
      <div className="block">
        {gate.locked ? (
          <Gate title="Your computer is almost ready.">Sign-in needs your user on the computer. {togo(f.lines, 'user')}</Gate>
        ) : agent === 'codex' ? (
          f.codex?.loggedIn ? (
            <Verify title="Signed in. Codex is ready on your computer." sub={f.codex.method === 'apiKey' ? 'with an API key' : 'with ChatGPT'} />
          ) : (
            <CodexSignIn />
          )
        ) : f.claude?.loggedIn && !other ? (
          <Verify title="Signed in. Claude is ready on your computer." sub={f.claude.email ? `as ${f.claude.email}` : undefined} />
        ) : (
          <>
            {other && <Notice>Claude on your computer is signed in as {other}. If that isn't you, sign in again with your own account.</Notice>}
            <ClaudeSignIn />
          </>
        )}
      </div>
      <LockNote>Your login stays on your computer. We never see it.</LockNote>
    </Sheet>
  )
}

type CodexPhase = { kind: 'idle' } | { kind: 'await'; login: CodexLogin } | { kind: 'expired' }

/**
 * Codex by device code: we show the link and the one-time code, the person signs in to ChatGPT on any device, and we
 * ask the computer every few seconds until Codex says signed in. Nothing is pasted back.
 */
function CodexSignIn() {
  const f = useFounder()
  const [phase, setPhase] = useState<CodexPhase>({ kind: 'idle' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
  useEffect(() => () => clearInterval(timer.current), [])

  async function start() {
    setBusy(true)
    setError(undefined)
    try {
      const login = await f.api.codexLogin()
      setPhase({ kind: 'await', login })
      clearInterval(timer.current)
      timer.current = setInterval(async () => {
        const s = await f.refreshCodex()
        if (s?.loggedIn) {
          clearInterval(timer.current)
          void f.reloadSetup()
        } else if (s && !s.pending) {
          clearInterval(timer.current)
          setPhase({ kind: 'expired' })
        }
      }, 3000)
    } catch (e) {
      setError(e instanceof ApiError && e.code === 'no_user' ? "Your own space on the computer isn't set up yet. We'll let you know when it is." : "Your computer couldn't start the sign-in just now. Try again in a moment.")
    } finally {
      setBusy(false)
    }
  }

  if (phase.kind === 'await') {
    return (
      <>
        <p>
          <b>Open the sign-in page, sign in to ChatGPT, and enter this code.</b>
        </p>
        <p>
          <Mono>{phase.login.code}</Mono> <CopyButton value={phase.login.code} label="Copy code" />
        </p>
        <a className="btn btn-ink" href={phase.login.url} target="_blank" rel="noreferrer">
          Open the sign-in page <Out size={14} />
        </a>
        <Gate title="Waiting for you to finish.">The code works for 15 minutes. This page ticks by itself once Codex is signed in.</Gate>
      </>
    )
  }
  return (
    <>
      {phase.kind === 'expired' && <Notice>That code ran out before the sign-in finished. Start again for a fresh one.</Notice>}
      <button className="btn btn-ink" type="button" onClick={start} disabled={busy}>
        {busy ? 'Asking your computer…' : 'Sign in to Codex'}
      </button>
      <p>First, in ChatGPT, turn on device code login: Settings, then Security. It lets your computer sign in without a browser of its own.</p>
      {error && <Notice>{error}</Notice>}
    </>
  )
}

/**
 * Requests to send from Paseo: pick one (or the first is picked already), and "Open Paseo" copies it on the way.
 * Setup's build step offers one; the finish offers the bigger ones.
 */
function Asks({ asks, label }: { asks: readonly Ask[]; label: string }) {
  const [pick, setPick] = useState<number | null>(asks.length === 1 ? 0 : null)
  const [copied, setCopied] = useState(false)
  const open = () => {
    const p = pick === null ? null : asks[pick]
    if (p) void navigator.clipboard?.writeText(p.full).then(() => setCopied(true), () => {})
  }
  return (
    <>
      <div className={`block prompts${asks.length === 1 ? ' one' : ''}`} role="radiogroup" aria-label={label}>
        {asks.map((p, i) => (
          <button key={p.tag} className="prompt" type="button" role="radio" aria-checked={pick === i} onClick={() => (setPick(i), setCopied(false))}>
            <span>{asks.length === 1 ? p.full : p.short}</span>
            <span className="label">{p.tag}</span>
          </button>
        ))}
      </div>
      <div className="row-btns block">
        <a className={`btn btn-ink${pick === null ? ' is-off' : ''}`} href={PASEO_WEB} target="_blank" rel="noreferrer" onClick={open} aria-disabled={pick === null}>
          Open Paseo <Out />
        </a>
        <span className="aside">{pick === null ? 'Pick one first. We copy it for you.' : copied ? 'Copied. Paste it into Paseo.' : 'We copy your request so you can paste it in.'}</span>
      </div>
    </>
  )
}

/** The first request needs only Shopify, so it comes after Connect your data. */
function BuildStep() {
  const f = useFounder()
  const path = f.personSetup.path
  const connected = stepDone('data', f.setup)
  return (
    <Sheet step="build" nav={<StepNav step="build" canContinue />}>
      <h1 id="h-build">{path === 'adopt' ? 'Change something in your app.' : path === 'whiteglove' ? 'Change something on your site.' : 'Build a first screen.'}</h1>
      <p className="lede">
        {connected
          ? "Start with Today, the screen your app opens on. Send this from Paseo and your agent swaps the example numbers for your store's."
          : 'Start with Today, the screen your app opens on. It shows example numbers until Shopify is connected; send this from Paseo once it is.'}
      </p>
      <Asks asks={[FIRST_ASK]} label="First request" />
      <div className="block">
        <div className="block-title">Your preview</div>
        <PreviewNote empty="Your preview address shows here once your computer is ready." />
      </div>
    </Sheet>
  )
}

/** Shopify is company-level: whoever has the keys connects it once, and everyone's step shows who and when. */
function DataStep() {
  const f = useFounder()
  const st = stepState('data', f.setup)
  const conns = useLoad(() => f.api.connections(), [f.api])
  const shopify = conns.data?.find((c) => c.key === 'shopify') ?? null
  const [justConnected, setJustConnected] = useState<string | null>(null)
  const connected = justConnected ?? (shopify?.status === 'connected' ? (shopify.detail ?? 'your store') : null)
  const when = doneOn(st.at)
  const adoptSkip = st.status === 'skipped' && st.how === 'observed'
  return (
    <Sheet step="data" nav={<StepNav step="data" canContinue={Boolean(connected) || adoptSkip} later />}>
      <h1 id="h-data">Connect your data.</h1>
      <p className="lede">
        {adoptSkip
          ? 'Your app already reads its own keys, so there is nothing to connect here. Connections has Shopify if you want it later.'
          : 'A few short steps in Shopify, then paste what it gives you here. Your app then reads your real orders, products and customers.'}
      </p>
      {connected ? (
        <div className="block">
          <ShopifyConnected name={connected} warnings={shopify?.warnings} scopes={shopify?.scopes} checkedAt={shopify?.checkedAt} onChecked={conns.reload} />
          {st.how === 'observed' && st.by && <p>Connected by {st.by === f.me.person.email ? 'you' : st.by}{when ? ` on ${when}` : ''}. It's the same for everyone on your team.</p>}
        </div>
      ) : adoptSkip ? null : (
        <ShopifySetup connection={shopify} onConnected={(name) => (setJustConnected(name), conns.reload(), void f.reloadSetup())} error={conns.error} />
      )}
    </Sheet>
  )
}

/** The guide and the form, defaulting to a Dev Dashboard app; a link switches to an older custom app's token. */
export function ShopifySetup({ connection, onConnected, error }: { connection: Connection | null; onConnected: (name: string) => void; error?: Error }) {
  const [mode, setMode] = useState<ShopifyMode>(connection?.status === 'error' && connection.mode === 'accessToken' ? 'accessToken' : 'clientCredentials')
  return (
    <>
      {connection?.status === 'error' && <div className="block"><Notice>Your store stopped answering with the saved key. Connect it again below.</Notice></div>}
      {error && <div className="block"><Notice>We couldn't check your connections just now. You can still connect below.</Notice></div>}
      <div className="block">
        <ShopifyGuide mode={mode} />
      </div>
      <div className="block">
        <ShopifyConnect connection={connection} mode={mode} onMode={setMode} onConnected={onConnected} />
      </div>
    </>
  )
}

function ShipStep() {
  const f = useFounder()
  const nav = useNavigate()
  const host = addressOf(f.state.world)
  const live = lineOk(f.lines, 'address')
  const again = tutorialMode(f.setup)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])
  async function finish() {
    setBusy(true)
    await Promise.all([f.mark('ship', 'done'), f.complete()]).catch(() => {})
    nav('/home')
  }
  return (
    <Sheet step="ship" nav={<StepNav step="ship" canContinue={false} />}>
      <h1 id="h-ship">Ship it.</h1>
      <p className="lede">When a change looks right in your preview, say “ship” to your agent in Paseo. It goes live at your address, and we keep watching its health.</p>
      <div className="block chips">
        {host && live ? (
          <a className="url-chip" href={`https://${host}`} target="_blank" rel="noreferrer">
            {host} <Out />
          </a>
        ) : (
          <span className="aside">{host ? `${host} goes live in a minute or so.` : 'Your address shows here once it is live.'}</span>
        )}
      </div>
      <div className="finish">
        <h2>{again ? "That's the tour." : "You're set up."}</h2>
        <p>Here's what happens from here.</p>
        <ul>
          <li>We keep your computer healthy and watch your app.</li>
          <li>Build whenever you like, from Paseo on any device.</li>
          <li>Run setup again from your dashboard whenever you want the tour.</li>
        </ul>
        <p>
          <b>Bigger asks, for later.</b> These need more than Shopify: your retailers, your lead times, your co-packer. Your
          agent asks for what it's missing.
        </p>
        <Asks asks={LATER_ASKS} label="Bigger requests" />
        <div className="row-btns pad-top">
          <button className="btn btn-ink" type="button" onClick={finish} disabled={busy}>
            Go to your dashboard
          </button>
        </div>
      </div>
    </Sheet>
  )
}
